import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import type { IConfigArea } from '@d-orca/shared/trusstor';
import { Checkbox } from '@components/ui/Checkbox/Checkbox';
import { useFloorViewSvg } from '@d-orca/shared/client/hooks';
import { SelectButton } from './SelectButton';
import {
  addPin as addPinTo,
  areaKey as buildKey,
  buildFloorGroups,
  clearFloor as clearFloorOf,
  floorSelection,
  locationsToState,
  stateToLocations,
  toggleArea as toggleAreaOf,
  type IFloorGroup as FloorGroup,
  type IssueLocation,
  type ISelectionState,
  type Pin,
} from '@d-orca/shared/client/issues';
import { FloorPlanCanvas } from './FloorPlanCanvas';
import { isImeComposing } from '@utils/ime';
import { ScrollArea } from '@components/ui/ScrollArea/ScrollArea';
import styles from './LocationSelector.module.css';

// ── Types ──────────────────────────────────────────────────────────────────

interface ILocationSelectorProps {
  value: IssueLocation[];
  onChange: (v: IssueLocation[]) => void;
  options: readonly IConfigArea[];
  /** No picking while the site's areas are loading or failed to load: the
   *  picker would show nothing to pick and resolve none of `value`. */
  disabled?: boolean;
  compact?: boolean;
  /** Unused — kept for API compatibility with other metadata fields. */
  selectStyle?: React.CSSProperties;
}

type TabId = 'floorPlan' | 'list';

// ── Component ──────────────────────────────────────────────────────────────

export function LocationSelector({ value, onChange, options, disabled }: ILocationSelectorProps) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabId>('list');
  // Collapsed state per floor
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  // Internal selection (picked areas + their pins) while the popper is open.
  const [selection, setSelection] = useState<ISelectionState>(() => locationsToState([]));
  const { selected, pins } = selection;
  // Floor Plan tab — drilled-into floor (null = floor list view)
  const [drilledFloorId, setDrilledFloorId] = useState<string | null>(null);

  const triggerRef = useRef<HTMLButtonElement>(null);
  const popperRef = useRef<HTMLDivElement>(null);

  const floorGroups = useMemo(() => buildFloorGroups(options), [options]);

  // Open the popper and seed internal state from the current value.
  // Done as an event-driven action (not a useEffect) so the React Compiler
  // purity check is satisfied — setState during effects causes cascading
  // renders.
  const openAndSync = useCallback(() => {
    setSelection(locationsToState(value));
    setDrilledFloorId(null);
    setOpen(true);
  }, [value]);

  // Commit and close — reads current state from the explicit args because
  // closures over the latest state aren't safe inside the outside-click
  // handler (registered once per render).
  const commitAndClose = useCallback(
    (s: ISelectionState) => {
      onChange(stateToLocations(s, options, value));
      setOpen(false);
    },
    [onChange, options, value],
  );

  // Outside-click handler
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        !triggerRef.current?.contains(target) &&
        !popperRef.current?.contains(target)
      ) {
        commitAndClose(selection);
      }
    };
    window.addEventListener('mousedown', handler);
    return () => window.removeEventListener('mousedown', handler);
  }, [open, selection, commitAndClose]);

  const handleDone = () => commitAndClose(selection);

  const addPin = useCallback(
    (floorId: string, areaId: string, pin: Pin) => setSelection((prev) => addPinTo(prev, options, floorId, areaId, pin)),
    [options],
  );

  const clearFloor = useCallback(
    (floorId: string) => {
      const floor = floorGroups.find((f) => f.floorId === floorId);
      if (floor) setSelection((prev) => clearFloorOf(prev, floor));
    },
    [floorGroups],
  );

  const toggleArea = (floorId: string, area: IConfigArea) =>
    setSelection((prev) => toggleAreaOf(prev, floorId, area.areaId));

  const toggleFloorCollapse = (floorId: string) => {
    setCollapsed((prev) => ({ ...prev, [floorId]: !prev[floorId] }));
  };

  const count = value.length;
  const displayText = count > 0
    ? t('issues.field.locationCount', { count })
    : null;

  return (
    <div className={styles.container}>
      {/* Trigger */}
      <SelectButton
        ref={triggerRef}
        value={displayText}
        placeholder={t('issues.field.locationPlaceholder')}
        onClick={() => (open ? setOpen(false) : openAndSync())}
        disabled={disabled}
      />

      {/* Popper */}
      {open && (
        <div
          ref={popperRef}
          role="dialog"
          aria-modal="true"
          aria-label={t('issues.field.location')}
          className={styles.popper}
          onKeyDown={(e) => {
            // Escape closes the picker (keeping its picks), not the form
            // around it — the form's own Escape skips a handled key.
            if (e.key !== 'Escape' || isImeComposing(e)) return;
            e.preventDefault();
            commitAndClose(selection);
          }}
        >
          {/* Top bar: Done + "Add Location" heading */}
          <div className={styles.popperHeader}>
            <button
              type="button"
              className={styles.doneBtn}
              onClick={handleDone}
            >
              {t('issues.action.done')}
            </button>
            <span className={styles.popperTitle}>{t('issues.field.locationAdd')}</span>
          </div>

          {/* Floor Plan / List tab switcher */}
          <div className={styles.tabSwitcher}>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'floorPlan' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('floorPlan')}
            >
              {t('issues.field.locationTabFloorPlan')}
            </button>
            <button
              type="button"
              className={`${styles.tabBtn} ${activeTab === 'list' ? styles.tabBtnActive : ''}`}
              onClick={() => setActiveTab('list')}
            >
              {t('issues.field.locationTabList')}
            </button>
          </div>

          {/* Tab content */}
          {activeTab === 'floorPlan' ? (
            <FloorPlanTab
              floorGroups={floorGroups}
              selected={selected}
              pins={pins}
              drilledFloorId={drilledFloorId}
              onDrillIn={setDrilledFloorId}
              onDrillOut={() => setDrilledFloorId(null)}
              onAddPin={addPin}
              onClearFloor={clearFloor}
            />
          ) : (
            /* Scrollable floor/area list */
            <ScrollArea className={styles.listBody}>
              {floorGroups.length === 0 && (
                <p className={styles.emptyMsg}>{t('issues.field.selectLocation')}</p>
              )}
              {floorGroups.map((floor) => {
                const isCollapsed = !!collapsed[floor.floorId];
                return (
                  <div key={floor.floorId} className={styles.floorGroup}>
                    {/* Floor header — collapsible */}
                    <button
                      type="button"
                      className={styles.floorHeader}
                      onClick={() => toggleFloorCollapse(floor.floorId)}
                      aria-expanded={!isCollapsed}
                    >
                      <svg
                        className={`${styles.floorChevron} ${isCollapsed ? styles.floorChevronCollapsed : ''}`}
                        width="12"
                        height="12"
                        viewBox="0 0 12 12"
                        aria-hidden="true"
                        focusable="false"
                      >
                        <path
                          d="M3 4.5L6 7.5L9 4.5"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      <span className={styles.floorNick}>{floor.floorNick}</span>
                    </button>

                    {/* Area rows */}
                    {!isCollapsed && (
                      <div className={styles.areaList}>
                        {floor.areas.map((area) => {
                          const k = buildKey(floor.floorId, area.areaId);
                          const checked = selected.has(k);
                          return (
                            <button
                              key={area.areaId}
                              type="button"
                              className={styles.areaRow}
                              onClick={() => toggleArea(floor.floorId, area)}
                              aria-pressed={checked}
                            >
                              <Checkbox
                                checked={checked}
                                ariaLabel={area.areaNick}
                                presentational
                              />
                              <span className={styles.areaNick}>{area.areaNick}</span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
}

// ── Floor Plan tab ─────────────────────────────────────────────────────────

interface IFloorPlanTabProps {
  floorGroups: FloorGroup[];
  selected: ReadonlySet<string>;
  pins: Readonly<Record<string, Pin[]>>;
  drilledFloorId: string | null;
  onDrillIn: (floorId: string) => void;
  onDrillOut: () => void;
  onAddPin: (floorId: string, areaId: string, pin: Pin) => void;
  onClearFloor: (floorId: string) => void;
}

/**
 * Mirrors trusstor_dashboard's `LocationFloorPlanSelector` flow:
 *   1. Floor list with per-floor selected-area counters.
 *   2. Drill into a floor → tappable plan SVG. Each tap drops a pin at the
 *      exact location, auto-selecting the underlying area.
 */
function FloorPlanTab({
  floorGroups,
  selected,
  pins,
  drilledFloorId,
  onDrillIn,
  onDrillOut,
  onAddPin,
  onClearFloor,
}: IFloorPlanTabProps) {
  const { t } = useTranslation();

  const floor = drilledFloorId
    ? floorGroups.find((f) => f.floorId === drilledFloorId) ?? null
    : null;

  // If the drilled floor disappears (areas refetched mid-flight), bounce
  // back to the list. Done in an effect — calling setState during render
  // from another component throws under React 19.
  useEffect(() => {
    if (drilledFloorId !== null && !floor) onDrillOut();
  }, [drilledFloorId, floor, onDrillOut]);

  if (drilledFloorId === null) {
    if (floorGroups.length === 0) {
      return <div className={styles.floorPlanPlaceholder}>{t('issues.field.selectLocation')}</div>;
    }
    return (
      <ScrollArea className={styles.floorList} viewportClassName={styles.floorListBody}>
        {floorGroups.map((floor) => {
          const count = floor.areas.reduce(
            (n, a) => n + (selected.has(buildKey(floor.floorId, a.areaId)) ? 1 : 0),
            0,
          );
          return (
            <button
              key={floor.floorId}
              type="button"
              className={styles.floorListRow}
              onClick={() => onDrillIn(floor.floorId)}
            >
              <span className={styles.floorListNick}>{floor.floorNick}</span>
              {count > 0 && (
                <span className={styles.floorListBadge}>
                  {/* The plural translation already interpolates {{count}};
                      the singular one doesn't, so prepend it there. */}
                  {count === 1
                    ? `${count} ${t('issues.field.area')}`
                    : t('issues.field.areasN', { count })}
                </span>
              )}
            </button>
          );
        })}
      </ScrollArea>
    );
  }

  if (!floor) return null;
  return (
    <FloorDetail
      floor={floor}
      selected={selected}
      pins={pins}
      onAddPin={onAddPin}
      onClearFloor={onClearFloor}
      onBack={onDrillOut}
    />
  );
}

interface IFloorDetailProps {
  floor: FloorGroup;
  selected: ReadonlySet<string>;
  pins: Readonly<Record<string, Pin[]>>;
  onAddPin: (floorId: string, areaId: string, pin: Pin) => void;
  onClearFloor: (floorId: string) => void;
  onBack: () => void;
}

function FloorDetail({ floor, selected, pins, onAddPin, onClearFloor, onBack }: IFloorDetailProps) {
  const { t } = useTranslation();
  const { siteId } = useParams<{ siteId: string }>();
  const { data: svg, isLoading, isError } = useFloorViewSvg(siteId ?? null, floor.floorId);

  // Slice global state down to just this floor for the canvas.
  const { selectedAreaIds: selectedAreaIdsThisFloor, pinsByArea: pinsThisFloor } = useMemo(
    () => floorSelection({ selected, pins }, floor),
    [selected, pins, floor],
  );

  const hasAnyPin = Object.values(pinsThisFloor).some((list) => list.length > 0);

  return (
    <div className={styles.floorDetail}>
      <div className={styles.floorDetailHeader}>
        <button type="button" className={styles.floorDetailBack} onClick={onBack}>
          ← {floor.floorNick}
        </button>
        {selectedAreaIdsThisFloor.size > 0 && (
          <button
            type="button"
            className={styles.floorDetailClear}
            onClick={() => onClearFloor(floor.floorId)}
          >
            {t('issues.action.clear')}
          </button>
        )}
      </div>
      <div className={styles.floorDetailSvgWrap}>
        {isLoading && (
          <span className={styles.floorDetailMsg}>{t('issues.action.loading')}</span>
        )}
        {isError && (
          <span className={styles.floorDetailMsg}>{t('issues.field.floorPlanLoadError')}</span>
        )}
        {/* fetchFloorViewSvg returns null on a 404 — render an explicit
            "no plan available" state so the user isn't staring at a blank box. */}
        {!isLoading && !isError && svg === null && (
          <span className={styles.floorDetailMsg}>{t('issues.field.noFloorPlanAvailable')}</span>
        )}
        {svg && (
          <FloorPlanCanvas
            svg={svg}
            pinsByArea={pinsThisFloor}
            selectedAreaIds={selectedAreaIdsThisFloor}
            onAddPin={(areaId, pin) => onAddPin(floor.floorId, areaId, pin)}
          />
        )}
        {svg && !hasAnyPin && (
          <div className={styles.floorDetailHint}>
            {t('issues.field.tapToPin')}
          </div>
        )}
      </div>
    </div>
  );
}
